import React, { useEffect, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, FormHelperText, Grid, TextField, Typography } from "@mui/material"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CreateUpdateProductFormValues } from "../productsTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { sanitizeAndLimitInput } from "../../../../../shared/components/createOrder/components/form/helpers"
import { INVALID_INPUT_KEY } from "../../../../nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"

const useStyles = makeStyles()((theme) => ({
    limitsTitle: {
        color: theme.palette.tailwind.slate[600],
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "0.875rem 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "0.75rem",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
        width: "100%",
    },
    input: {
        textAlign: "right",
        maxWidth: "4rem",
        height: "1.5rem",
        padding: "0.5rem 0.75rem",
    },
}))

type ItemLimitsFieldProps = Readonly<{}>

interface FormikErrors {
    itemLimits?: {
        maxDailyItemsPerPerson?: string
        minItemsPerOrder?: string
    }
}

export function ItemLimitsField({}: ItemLimitsFieldProps) {
    const { classes } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<
        CreateUpdateProductFormValues & { errors: FormikErrors }
    >()

    const [limitsErrors, setLimitsErrors] = useState<{ max: string; min: string }>({ max: "", min: "" })

    useEffect(() => {
        if (typeof errors.itemLimits === "object" && errors.itemLimits !== null) {
            const { maxDailyItemsPerPerson, minItemsPerOrder } = errors.itemLimits
            setLimitsErrors({ max: maxDailyItemsPerPerson || "", min: minItemsPerOrder || "" })
        } else setLimitsErrors({ max: "", min: "" })
    }, [errors.itemLimits])

    return (
        <>
            <Grid container gap={1.5} flexDirection="column">
                <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={2}>
                    <LocalizedStrict id="product-create-item-limits-max-field">
                        <Typography variant="body1" className={classes.limitsTitle}>
                            Max. daily items per person:
                        </Typography>
                    </LocalizedStrict>
                    <TextField
                        type="number"
                        name="itemLimits.maxDailyItemsPerPerson"
                        value={values.itemLimits?.maxDailyItemsPerPerson ?? ""}
                        onChange={(event) => {
                            const value = event.target.value
                            setFieldValue("itemLimits.maxDailyItemsPerPerson", value === "" ? undefined : value)
                        }}
                        inputProps={{
                            className: classes.input,
                            inputMode: "numeric" as const,
                            onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                                if (e.currentTarget.value !== sanitized) {
                                    e.currentTarget.value = sanitized
                                }
                            },
                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (INVALID_INPUT_KEY.includes(e.key)) {
                                    e.preventDefault()
                                }
                            },
                        }}
                        error={!!limitsErrors.max}
                    />
                </Grid>
                {!!limitsErrors.max && <FormHelperText error>{limitsErrors.max}</FormHelperText>}
                <Box className={classes.infoWrap}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict id="product-create-item-limits-max-info">
                        <Typography variant="body2">
                            This determines how many items total of this type a customer can order per day.
                        </Typography>
                    </LocalizedStrict>
                </Box>
            </Grid>
            <Grid container gap={1.5} flexDirection="column">
                <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={2}>
                    <LocalizedStrict id="product-create-item-limits-min-field">
                        <Typography variant="body1" className={classes.limitsTitle}>
                            Min. items per order:
                        </Typography>
                    </LocalizedStrict>
                    <TextField
                        type="number"
                        name="itemLimits.minItemsPerOrder"
                        value={values.itemLimits?.minItemsPerOrder ?? ""}
                        onChange={(event) => {
                            const value = event.target.value
                            setFieldValue("itemLimits.minItemsPerOrder", value === "" ? undefined : value)
                        }}
                        inputProps={{
                            className: classes.input,
                            inputMode: "numeric" as const,
                            onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                                if (e.currentTarget.value !== sanitized) {
                                    e.currentTarget.value = sanitized
                                }
                            },
                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (INVALID_INPUT_KEY.includes(e.key)) {
                                    e.preventDefault()
                                }
                            },
                        }}
                        error={!!limitsErrors.min}
                    />
                </Grid>
                {!!limitsErrors.min && <FormHelperText error>{limitsErrors.min}</FormHelperText>}
                <Box className={classes.infoWrap}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict id="product-create-item-limits-min-info">
                        <Typography variant="body2">
                            This determines minimum number of items of this type a customer has to add to an order.
                        </Typography>
                    </LocalizedStrict>
                </Box>
            </Grid>
            {!!errors.itemLimits && typeof errors.itemLimits === "string" && (
                <FormHelperText error>{errors.itemLimits}</FormHelperText>
            )}
        </>
    )
}
